import type { i18n } from "i18next";

import type { AppFeatureDefinition, Language } from "@nasam/kernel";
import { isStaticNamespace } from "./resources";

const instanceModules = new WeakMap<i18n, Map<string, AppFeatureDefinition>>();

function resolveInstanceLanguage(instance: i18n): Language {
  const current = instance.language;
  if (current === "ar" || current === "en") {
    return current;
  }
  const resolved = instance.resolvedLanguage;
  if (resolved === "ar" || resolved === "en") {
    return resolved;
  }
  return "en";
}

export function registerInstanceModule(
  instance: i18n,
  module: AppFeatureDefinition,
): void {
  let moduleMap = instanceModules.get(instance);
  if (!moduleMap) {
    moduleMap = new Map<string, AppFeatureDefinition>();
    instanceModules.set(instance, moduleMap);
  }
  moduleMap.set(module.id, module);
}

export function getRegisteredI18nModules(
  instance: i18n,
): readonly AppFeatureDefinition[] {
  const moduleMap = instanceModules.get(instance);
  return moduleMap ? Array.from(moduleMap.values()) : [];
}

export type LoadModuleI18nLanguageOption =
  | Language
  | {
      readonly language?: Language;
    };

export async function loadModuleI18n(
  instance: i18n,
  module: AppFeatureDefinition,
  languageOrOptions?: LoadModuleI18nLanguageOption,
): Promise<boolean> {
  if (!module.i18n) {
    return false;
  }

  const { namespace, load } = module.i18n;

  if (isStaticNamespace(namespace)) {
    console.warn(
      `[i18n] Module "${module.id}" declared namespace "${namespace}" which collides with a static host namespace. The static bundle will be preserved.`,
    );
    return false;
  }

  registerInstanceModule(instance, module);

  const targetLanguage =
    typeof languageOrOptions === "string"
      ? languageOrOptions
      : (languageOrOptions?.language ?? resolveInstanceLanguage(instance));

  const loader = load[targetLanguage];
  if (typeof loader !== "function") {
    console.warn(
      `[i18n] Module "${module.id}" has no translation loader for language "${targetLanguage}".`,
    );
    return false;
  }

  try {
    const bundle = await loader();
    const resources = bundle.default;

    instance.addResourceBundle(
      targetLanguage,
      namespace,
      resources,
      true,
      true,
    );
    return true;
  } catch (error) {
    console.error(
      `[i18n] Failed to load translations for module "${module.id}" (${targetLanguage}):`,
      error,
    );
    return false;
  }
}

export async function loadModulesI18n(
  instance: i18n,
  modules: readonly AppFeatureDefinition[],
  language: Language,
): Promise<void> {
  await Promise.all(
    modules.map((module) => loadModuleI18n(instance, module, language)),
  );
}
